import * as React from 'react';
import { useEmojiShortcode } from '../../../hooks/use-emoji-shortcode';

/** Round portrait: the colour collage on a cream disc (or a letter when there is no picture). */
export function StoryPortrait({
  url,
  fallback,
  color,
  size,
  alt = '',
}: {
  url?: string | null;
  fallback: string;
  color: string;
  size: number;
  alt?: string;
}) {
  if (url) {
    return (
      <div className="zs-pp" style={{ width: size, height: size, border: `${size >= 32 ? 2.5 : 1}px solid ${color}55` }}>
        <img src={url} alt={alt} draggable={false} />
      </div>
    );
  }
  return (
    <div
      className="zs-pp zs-st"
      aria-hidden="true"
      style={{ width: size, height: size, border: `2.5px solid ${color}35`, color, fontSize: size * 0.52, background: `${color}12` }}
    >
      {fallback}
    </div>
  );
}

/** The seal ("selo") — same drawing as the one stamped on the wall posters. */
export function StorySeal({
  symbol,
  label,
  background,
  size,
  rotate = 12,
}: {
  symbol?: string | null;
  label?: string | null;
  background: string;
  size: number;
  rotate?: number;
}) {
  const glyph = useEmojiShortcode(symbol);
  return (
    <div
      className="zs-selo"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        background,
        outline: `2px solid ${background}`,
        transform: `rotate(${rotate}deg)`,
        borderWidth: Math.max(2, size * 0.036),
      }}
    >
      <i className="zs-ri" style={{ inset: Math.max(3, size * 0.06) }} />
      <span className="zs-g" style={{ fontSize: size * 0.44 }}>
        {glyph || '✦'}
      </span>
      {label ? (
        <span className="zs-l" style={{ fontSize: Math.max(7, size * 0.13), marginTop: Math.max(1, size * 0.03) }}>
          {label}
        </span>
      ) : null}
    </div>
  );
}

export function StoryTape({
  left,
  right,
  top,
  rotate,
  width,
  color,
}: {
  left?: number;
  right?: number;
  top: number;
  rotate: number;
  width: number;
  color: string;
}) {
  return <div className="zs-tape" aria-hidden="true" style={{ left, right, top, width, background: color, transform: `rotate(${rotate}deg)` }} />;
}

const svg = { viewBox: '0 0 24 24', 'aria-hidden': true } as const;
const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2.4 } as const;

export const StoryIcon = {
  X: () => (<svg {...svg} {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>),
  Pause: () => (<svg {...svg} fill="currentColor"><rect x="6" y="5" width="4" height="14" /><rect x="14" y="5" width="4" height="14" /></svg>),
  Play: () => (<svg {...svg} fill="currentColor"><path d="M7 4.5v15l13-7.5z" /></svg>),
  Replay: () => (<svg {...svg} {...stroke}><path d="M4 12a8 8 0 1 0 2.5-5.8M4 4v5h5" /></svg>),
  Left: () => (<svg {...svg} {...stroke}><path d="M15 5l-7 7 7 7" /></svg>),
  Right: () => (<svg {...svg} {...stroke}><path d="M9 5l7 7-7 7" /></svg>),
};
